import React, { forwardRef } from "react";
import { useInView } from "react-intersection-observer";
import { StaticImage } from "gatsby-plugin-image";

// components
import ReusableForm from "../../Global/ReusableForm/ReusableForm";

// css
import * as styles from "./FormSection.module.css";

const FormSectionComponent = forwardRef(
  (
    {
      onFormSubmit,
      mixpanelPageName = "For Employers Page",
      buttonColor = "#AD2576",
      checkboxColor = "#AD2576",
      loading = false,
      deviceType = "Desktop",
      operatingSystem = "MacOs",
    },
    ref
  ) => {
    const { ref: inViewRef, inView } = useInView({
      triggerOnce: true,
      threshold: 0.3,
    });

    // Define form fields
    const formFields = [
      {
        name: "organizationName",
        placeholder: "Organization Name*",
        type: "text",
        colSize: 12,
        validation: {
          required: "Please enter your organization name",
          pattern: {
            value: /[a-zA-Z0-9][a-zA-Z0-9 ]*/, // eslint-disable-line no-useless-escape
            message: "Please enter a valid organization name",
          },
          maxLength: {
            value: 100,
            message: "Organization name is too long",
          },
        },
      },
      {
        name: "fullName",
        placeholder: "Full Name*",
        type: "text",
        colSize: 6,
        validation: {
          required: "Please enter your full name",
          pattern: {
            value: /^[A-Za-z\s]+$/, // eslint-disable-line no-useless-escape
            message: "Please enter a valid name",
          },
          maxLength: {
            value: 50,
            message: "Name is too long",
          },
        },
      },
      {
        name: "mobileNumber",
        placeholder: "Mobile Number*",
        type: "text",
        inputMode: "numeric",
        maxLength: 10,
        colSize: 6,
        validation: {
          required: "Please enter your mobile number",
          pattern: {
            value: /^\+?([1-9]{1})\)?[-. ]?([0-9]{5})[-. ]?([0-9]{4})$/, // eslint-disable-line no-useless-escape
            message: "Please enter a valid mobile number",
          },
          maxLength: {
            value: 10,
            message: "Invalid mobile number",
          },
        },
      },
      {
        name: "emailId",
        placeholder: "Email Id*",
        type: "email",
        colSize: 12,
        validation: {
          required: "Please enter your email id",
          pattern: {
            value:
              /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i, // eslint-disable-line no-useless-escape
            message: "Please enter a valid email id",
          },
        },
      },
    ];

    // Handle form submission
    const handleFormSubmit = async data => {
      // Call parent callback if provided
      if (onFormSubmit) {
        await onFormSubmit(data);
      }
    };

    return (
      <section ref={ref} className={styles.formSectionWrapper}>
        <div
          ref={inViewRef}
          className={`container scroll_animate ${inView && "inview"}`}
        >
          <div className="row align-items-center">
            {/* Form Column */}
            <div className="col-lg-6 col-12 order-2 order-lg-1">
              <div className={styles.formContainer}>
                <div className={styles.formHeader}>
                  <h2 className={styles.formTitle}>
                    Improve workforce wellbeing & business ROI
                  </h2>
                  <p className={styles.formDescription}>
                    Learn how Twin Health partners with organisations to improve
                    employee wellbeing, reduce claims, and boost productivity.
                  </p>
                </div>

                <div className={styles.formContent}>
                  <h3 className={styles.formSubtitle}>Partner with us</h3>
                  <ReusableForm
                    fields={formFields}
                    onSubmit={handleFormSubmit}
                    ctaText="Submit"
                    loading={loading}
                    mixpanelPageName={mixpanelPageName}
                    formIsModal={false}
                    showCheckbox={true}
                    checkboxText="I agree to be contacted by Twin Health via Call, SMS, RCS, WhatsApp & Email"
                    buttonColor={buttonColor}
                    checkboxColor={checkboxColor}
                    deviceType={deviceType}
                    operatingSystem={operatingSystem}
                  />
                </div>
              </div>
            </div>

            {/* Image Column */}
            <div className="col-lg-6 col-12 order-1 order-lg-2">
              <div className={styles.imageContainer}>
                <StaticImage
                  src="../../../images/forEmployee/form_img.png"
                  alt="Employee Wellness"
                  className={styles.formImage}
                  placeholder="blurred"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

export default FormSectionComponent;
